(ns leg.login
  "Asking to be let in. One screen, not a wizard: an address, a button that
  asks for a code, and a field for the four digits that come back.

  There is no second screen for signing up, because there is no signing up
  here at all: accounts are made on the admin page. This screen does not
  know that, and must not - it says a code is on its way to whatever address
  was typed, because the server answers the same for an address it knows and
  one it does not (see leser.api/request-code). An address nobody added
  waits for a mail that never comes.

  Nothing is sent until it is asked for - drawing this screen is not the
  same as wanting a mail, and a reload should not cost anybody a message.

  It is the only screen in the window the server does not feed, because it
  has to work while the server is refusing to answer anything else."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [clojure.string :as str]
            [kc.core :as kc]
            [leg.api :as api]
            [leg.auth :as auth]
            [leg.mui :as mui :refer [sx]]
            [leg.state :as state]))

(defn- only-digits [s]
  (let [d (str/replace (or s "") #"[^0-9]" "")]
    (subs d 0 (min 4 (count d)))))

(defn- address?
  "Whether this is worth sending. Shallow on purpose, and the same check the
  server makes: the only proof that an address works is that a code sent to
  it comes back."
  [s]
  (boolean (re-matches #"[^@\s]+@[^@\s.]+\.[^@\s]+" (str/trim (or s "")))))

(def-ui Login []
  (let [[email set-email] (kc/use-state (or (auth/last-email) ""))
        [sent? set-sent]  (kc/use-state false)
        [code set-code]   (kc/use-state "")
        [busy? set-busy]  (kc/use-state false)
        [error set-error] (kc/use-state nil)
        send!   (fn []
                  (when (address? email)
                    (set-busy true)
                    (set-error nil)
                    (auth/remember-email! (str/trim email))
                    (-> (api/request-code! (str/trim email))
                        (.then (fn [_]
                                 (set-sent true)
                                 (set-busy false)
                                 ;; Asking is done with a button and answering
                                 ;; with the keyboard - whoever pressed it
                                 ;; should not have to reach back for the field.
                                 (some-> (.getElementById js/document "leser-code") .focus)))
                        (.catch (fn [_]
                                  (set-busy false)
                                  (set-error "The code could not be requested."))))))
        submit! (fn []
                  (set-busy true)
                  (set-error nil)
                  (-> (api/verify-code! (str/trim email) code)
                      ;; `start!` and not `load-state!`: somebody who has
                      ;; just signed in is a window coming up, and comes up
                      ;; where the settings say (`leg.state/start-pane!`).
                      (.then (fn [_] (set-busy false) (state/start!)))
                      (.catch (fn [_]
                                (set-busy false)
                                (set-code "")
                                (set-error "Wrong or expired code.")))))]
    ($ mui/Box {:sx (sx {:minHeight "100vh" :display "flex"
                         :alignItems "center" :justifyContent "center" :padding "1rem"})}
       ($ mui/Box {:sx (sx {:display "flex" :flexDirection "column" :gap ".9rem"
                            :padding "1.5rem" :width "100%" :maxWidth "20rem"
                            :border "1px solid" :borderColor "divider"
                            :borderRadius 2
                            :backgroundColor "background.paper"})}
          ($ mui/Typography {:variant "h6"} "Leser")
          ($ mui/Typography {:sx (sx {:color "text.secondary"})}
             (if sent?
               (str "A code is on its way to " (str/trim email) ".")
               "Type your e-mail address and a code goes to it."))
          ($ mui/TextField
             {:id "leser-email"
              :value email
              :label "E-Mail"
              :type "email"
              :size "small"
              :autoFocus (not sent?)
              :disabled sent?
              :autoComplete "email"
              :slotProps (sx {:htmlInput {:inputMode "email"
                                          :autoCapitalize "none"
                                          :spellCheck false}})
              :onChange (fn [^js e] (set-email (-> e .-target .-value)))
              :onKeyDown (fn [^js e]
                           (when (and (= "Enter" (.-key e)) (address? email))
                             (.preventDefault e)
                             (send!)))})
          ;; Four digits and nothing else. What the field lets in is said to
          ;; the phone as well as to the browser - a numeric keypad is what
          ;; comes up for this, and a full keyboard is four wrong keys.
          (when sent?
            ($ mui/TextField
               {:id "leser-code"
                :value code
                :placeholder "----"
                :autoFocus true
                :size "small"
                :autoComplete "one-time-code"
                :slotProps (sx {:htmlInput {:inputMode "numeric"
                                            :pattern "[0-9]*"
                                            :maxLength 4
                                            :style {:letterSpacing ".5em"
                                                    :textAlign "center"
                                                    :fontSize "1.4rem"}}})
                :onChange (fn [^js e] (set-code (only-digits (-> e .-target .-value))))
                :onKeyDown (fn [^js e]
                             (when (and (= "Enter" (.-key e)) (= 4 (count code)))
                               (.preventDefault e)
                               (submit!)))}))
          (when error
            ($ mui/Alert {:severity "error" :variant "outlined"} error))
          ($ mui/Box {:sx (sx {:display "flex" :alignItems "center" :gap ".5rem"})}
             ;; The same button either way, and it says which one it is: the
             ;; first press starts the whole thing, every one after it is
             ;; somebody who did not get the mail. Drawn as a button and not as
             ;; a piece of coloured text, because it is the one thing to do on
             ;; this screen - until there is a code to send, and then it is the
             ;; quiet one beside "Sign in".
             ($ mui/Button {:disabled (or busy? (not (address? email)))
                            :onClick (fn [_] (send!))
                            :variant (if sent? "outlined" "contained")
                            :disableElevation true
                            :color (if sent? "inherit" "primary")}
                (if sent? "New code" "Request code"))
             ($ :span {:style {:flex "1"}})
             (when sent?
               ($ mui/Button {:disabled (or busy? (not= 4 (count code)))
                              :variant "contained"
                              :disableElevation true
                              :onClick (fn [_] (submit!))}
                  "Sign in")))))))
